import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { Tabs } from '@components/ui/Tabs';
import styles from './DeliveryDetailDrawer.module.css';

/**
 * The Overview/Edit tab shell both record drawers (delivery, stock) share.
 *
 * The Edit panel is `keepMounted` with a lazy first mount: the hosted form
 * appears on the first Edit visit and then STAYS mounted (hidden) across tab
 * switches — Base UI unmounts an inactive panel by default, and the form's
 * state is its own, so a glance back at Overview mid-edit would silently
 * discard every unsaved keystroke. Lazy on purpose: until Edit is opened
 * there is no form in the DOM at all, so the read-only view carries no
 * hidden twin of every field.
 */
interface IDetailTabsProps {
  overview: ReactNode;
  /** Absent: the record has nothing to edit for this viewer (a synced
   *  delivery is history, or the caller's role cannot write), so there is no
   *  Edit tab at all — only Overview, which still reads the record in full. */
  edit?: ReactNode;
  /** Transient lock (the refresh-to-edit revision lock): the Edit tab (the
   *  form AND Delete live behind it) is disabled. A tab already visited is
   *  exempt: it holds the user's input, so the lock disables its Save, not
   *  the tab (spec ruling 5). */
  editDisabled?: boolean;
  /** Told when the Edit tab has been opened (a form is now mounted, holding
   *  input) and again when it goes away. */
  onEditVisitedChange?: (visited: boolean) => void;
}

export function DetailTabs({ overview, edit, editDisabled = false, onEditVisitedChange }: IDetailTabsProps) {
  const { t } = useTranslation();
  const [editVisited, setEditVisited] = useState(false);
  useEffect(() => {
    if (!editVisited) return undefined;
    onEditVisitedChange?.(true);
    return () => onEditVisitedChange?.(false);
  }, [editVisited, onEditVisitedChange]);
  return (
    <Tabs.Root
      defaultValue="overview"
      onValueChange={(value) => value === 'edit' && setEditVisited(true)}
      className={styles.tabsRoot}
    >
      <Tabs.List className={styles.tabsList}>
        <Tabs.Tab value="overview">{t('logistics.detail.tabs.overview')}</Tabs.Tab>
        {edit === undefined ? null : (
          /* A tab the user already opened holds their input — the lock
             disables its Save, not the tab (spec ruling 5). */
          <Tabs.Tab value="edit" disabled={editDisabled && !editVisited}>
            {t('logistics.detail.tabs.edit')}
          </Tabs.Tab>
        )}
        <Tabs.Indicator />
      </Tabs.List>
      <Tabs.Panel value="overview" className={styles.tabPanel}>
        <ScrollArea viewportClassName={styles.tabBody}>{overview}</ScrollArea>
      </Tabs.Panel>
      <Tabs.Panel value="edit" keepMounted className={styles.tabPanel}>
        <ScrollArea viewportClassName={styles.tabBody}>{editVisited ? edit : null}</ScrollArea>
      </Tabs.Panel>
    </Tabs.Root>
  );
}
